import { type RefObject, useRef } from "react";
import { useGlobals } from "@/components/providers/globals-provider.tsx";
import Icon from "@/components/ui/icon.tsx";
import { OverlaySpinner } from "@/components/ui/overlay.tsx";
import { TabContent, Tabs, TabsList, TabsProvider, TabsTrigger } from "@/components/ui/tabs.tsx";
import SmartNumber from "@/features/sub-families/components/smart-number.tsx";
import useModelCode from "@/features/sub-families/hooks/use-model-code.ts";
import ChangeFilterOptionProvider from "@/features/sub-families/providers/change-filter-option-provider.tsx";
import useTracking from "@/features/tracking/hooks/use-tracking.ts";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import { useAppSelector } from "@/redux/store.ts";
import type { Nullable } from "@/types";
import { cn } from "@/utils/style.ts";
import Breadcrumbs from "./components/breadcrumbs.tsx";
import SubFamilyFilterOptions from "./components/sub-family-filter-options.tsx";
import SubFamilyName from "./components/sub-family-name.tsx";
import Summary from "./components/summary.tsx";
import UsedDifferentSmartNumber from "./components/used-different-smart-number.tsx";
import { useSubFamiliesConfig } from "./providers/sub-families-config-provider.tsx";
import { ConfigurationTab } from "./types";
import { isSummaryTab } from "./utils/tabs.ts";

const TABS = [
    ConfigurationTab.Design,
    ConfigurationTab.Light,
    ConfigurationTab.Accessory,
    ConfigurationTab.Summary,
] as const;

type Props = { panelRef: RefObject<Nullable<HTMLDivElement>> };
export default function ConfigurationPanel({ panelRef }: Props) {
    const t = useTranslations();
    const trackEvent = useTracking();
    const modelCode = useModelCode();

    const ref = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const { configurationTab, clearProperty } = useGlobals();
    const { isLoading, isPlaceholderData } = useSubFamiliesConfig();

    const smartNumber = useAppSelector((state) => state.smartNumber.calculatedResult);

    const scrollToTop = () => {
        panelRef.current?.querySelector(".configuration-panel__scrollarea")?.scrollTo({ top: 0, behavior: "smooth" });
        panelRef.current?.querySelector(".tabs__content.active")?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onChangeTab = (tab: string) => {
        if (tab === ConfigurationTab.Summary) {
            trackEvent("3d-nav-summary", {
                component: "navigation",
                label: tab,
                model_code: modelCode ?? "",
                event: "configurator_submit",
                smartNumber: smartNumber ?? "",
            });
        } else {
            trackEvent("3d-nav-tab", {
                component: "navigation",
                label: tab,
                model_code: modelCode ?? "",
            });
        }

        clearProperty();
        scrollToTop();
    };

    return (
        <ChangeFilterOptionProvider>
            {(isLoading || isPlaceholderData) && <OverlaySpinner />}
            {!isLoading && (
                <TabsProvider initialTab={configurationTab ?? TABS[0]} onTabChange={onChangeTab}>
                    <div className="configuration-panel__content" ref={ref}>
                        <div className="configuration-panel__header" ref={headerRef}>
                            <Breadcrumbs />
                            <SubFamilyName />
                            <div className="configuration-panel__product-code-row">
                                <SmartNumber />
                                <span className="configuration-panel__product-code-separator">&bull;</span>
                                <TabsTrigger
                                    tab={ConfigurationTab.Summary}
                                    variant="link"
                                    className="configuration-panel__product-code"
                                    onClick={() => {
                                        scrollToTop();
                                    }}
                                >
                                    <span>{t("view_product_code")}</span>
                                </TabsTrigger>
                            </div>
                        </div>
                        <div>
                            <Tabs className="tabs--main">
                                <TabsList className="configuration-panel__tab-list">
                                    {TABS.map((tab, idx) => {
                                        const index = `${idx + 1}`.padStart(2, "0");
                                        const isSummary = isSummaryTab(tab);

                                        return (
                                            <TabsTrigger
                                                className={cn(
                                                    "configuration-panel__tab",
                                                    isSummary && "tabs__tab--summary",
                                                )}
                                                key={tab}
                                                tab={tab}
                                            >
                                                {isSummary ? <SummaryTab /> : <IndexTab index={index} label={tab} />}
                                            </TabsTrigger>
                                        );
                                    })}
                                </TabsList>
                                {TABS.map((tab, idx) => (
                                    <TabContent key={tab} tab={tab}>
                                        {isSummaryTab(tab) ? (
                                            <Summary smartNumber={smartNumber} />
                                        ) : (
                                            <>
                                                <SubFamilyFilterOptions tab={tab} />
                                                <TabsTrigger
                                                    tab={TABS[idx + 1]}
                                                    className="tabs__trigger--next-step"
                                                    background="accent"
                                                >
                                                    <span>{t("next_step")}</span>
                                                    <Icon name="arrow-right" />
                                                </TabsTrigger>
                                            </>
                                        )}
                                    </TabContent>
                                ))}
                            </Tabs>
                        </div>
                    </div>
                </TabsProvider>
            )}
            <UsedDifferentSmartNumber />
        </ChangeFilterOptionProvider>
    );
}

function IndexTab({ index, label }: { index: string; label: string }) {
    return (
        <>
            <span className="tabs__trigger-index">{index}</span>
            <span className="configuration-panel__tab-label">{label}</span>
        </>
    );
}

function SummaryTab() {
    const t = useTranslations();

    return (
        <>
            <span>{t("summary")}</span>
            <Icon name="check-circle" />
        </>
    );
}
